import {
  MailOutlined,
  PhoneOutlined,
  WhatsAppOutlined,
} from '@ant-design/icons';
import { SettingsActions } from '@portal/actions';
import useReduxState from '@portal/hooks/useReduxState';
import { CardsKeySettingsEnum } from '@portal/models/enumerators/cards';
import { translate } from '@portal/services/i18n';
import MessageService from '@portal/services/message';
import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import AdvancedButton from '@portal/components/AdvancedButton/AdvancedButton';
import GenericModal from '@portal/components/GenericModal/GenericModal';
import ThemedText from '@portal/components/ThemedText/ThemedText';
import './CardContact.less';

interface CardContactProps {
  visible: boolean;
  onClose: () => void;
}

interface ContactOptionProps {
  onPress: () => void;
  icon: React.ReactNode;
  title: string;
  description: string;
}

const CardContact = ({ visible, onClose }: CardContactProps) => {
  const dispatch = useDispatch();
  const {
    settings: { keys, error },
  } = useReduxState();

  useEffect(() => {
    dispatch(
      SettingsActions.getSettingsKeys([
        CardsKeySettingsEnum.CARD_CONTACT_EMAIL,
        CardsKeySettingsEnum.CARD_PHONE_NUMBER,
        CardsKeySettingsEnum.CARD_WHATSAPP_NUMBER,
      ])
    );
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      MessageService.error(error.message);
    }
  }, [error, dispatch]);

  const mappedSettings = (keys || []).reduce(
    (acc: Record<string, string>, item: Record<string, string>) => {
      return { ...acc, ...item };
    },
    {}
  );

  const PHONE_NUMBER = mappedSettings[CardsKeySettingsEnum.CARD_PHONE_NUMBER];
  const WHATSAPP_NUMBER =
    mappedSettings[CardsKeySettingsEnum.CARD_WHATSAPP_NUMBER];
  const EMAIL = mappedSettings[CardsKeySettingsEnum.CARD_CONTACT_EMAIL];

  const ContactOption = ({
    onPress,
    icon,
    title,
    description,
  }: ContactOptionProps) => {
    return (
      <div className="card-contact__option">
        <div className="card-contact__option__row">
          <div className="card-contact__option__icon">{icon}</div>
          <div className="card-contact__option__content">
            <ThemedText className="card-contact__option__title">
              {title}
            </ThemedText>
            <ThemedText className="card-contact__option__description">
              {description}
            </ThemedText>
          </div>
        </div>
        <AdvancedButton
          text={translate('COMPONENTS.CARD_CONTACT.BUTTON')}
          onClick={onPress}
          variant="outlined"
        />
      </div>
    );
  };

  const handlePhoneContact = async () => {
    if (!PHONE_NUMBER) return;

    const phone = PHONE_NUMBER.replace(/\D/g, '');

    const isMobile = /Android|iPhone|iPad|iPod|Windows Phone/i.test(
      navigator.userAgent
    );

    if (isMobile) {
      window.location.href = `tel:${phone}`;
      return;
    }

    navigator.clipboard.writeText(phone);
    MessageService.success('COMPONENTS.CARD_CONTACT.MESSAGES.PHONE_COPIED');
  };

  const handleWhatsAppContact = () => {
    if (!WHATSAPP_NUMBER) return;

    const phone = WHATSAPP_NUMBER.replace(/\D/g, '');
    const message = encodeURIComponent(
      'COMPONENTS.CARD_CONTACT.MESSAGES.TITLE'
    );

    window.open(
      `https://wa.me/${phone}?text=${message}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  const handleEmailContact = async () => {
    if (!EMAIL) return;

    const isMobile = /Android|iPhone|iPad|iPod|Windows Phone/i.test(
      navigator.userAgent
    );

    if (isMobile) {
      const subject = encodeURIComponent(
        translate('COMPONENTS.CARD_CONTACT.MESSAGES.MAIL_TITLE')
      );
      const body = encodeURIComponent(
        translate('COMPONENTS.CARD_CONTACT.MESSAGES.TITLE')
      );
      window.location.href = `mailto:${EMAIL}?subject=${subject}&body=${body}`;
      return;
    }

    navigator.clipboard.writeText(EMAIL);
    MessageService.success('COMPONENTS.CARD_CONTACT.MESSAGES.EMAIL_COPIED');
  };

  return (
    <GenericModal
      visible={visible}
      onClose={onClose}
      title={translate('COMPONENTS.CARD_CONTACT.TITLE')}
      description={translate('COMPONENTS.CARD_CONTACT.DESCRIPTION')}
    >
      <div>
        <ContactOption
          onPress={handlePhoneContact}
          icon={<PhoneOutlined />}
          title={translate('COMPONENTS.CARD_CONTACT.PHONE')}
          description={PHONE_NUMBER}
        />
        <ContactOption
          onPress={handleWhatsAppContact}
          icon={<WhatsAppOutlined />}
          title={translate('COMPONENTS.CARD_CONTACT.WHATSAPP')}
          description={WHATSAPP_NUMBER}
        />
        <ContactOption
          onPress={handleEmailContact}
          icon={<MailOutlined />}
          title={translate('COMPONENTS.CARD_CONTACT.EMAIL')}
          description={EMAIL}
        />
      </div>
    </GenericModal>
  );
};

export default CardContact;
